import { Avatar } from '@/components/ui/avatar';
import { Circle } from '@chakra-ui/react';
import { SparkSolid, WarningTriangle } from 'iconoir-react';
import { memo } from 'react';

type NotificationAvatarProps = {
  actorName: string;
  avatarUrl: string | null;
  isAgent: boolean;
  needsAttention: boolean;
};

const TILE_SIZE = '36px';

/** Initials fallback of the design system: pale rose, deep rose letters. */
const INITIALS_TINT = { bg: 'rose.200', color: 'rose.900' } as const;

const TILES = {
  attention: {
    bg: 'warning.surface',
    color: 'warning.text',
    borderColor: 'warning.border',
    Icon: WarningTriangle,
  },
  agent: {
    bg: 'lilac.muted',
    color: 'lilac.solid',
    borderColor: 'lilac.subtle',
    Icon: SparkSolid,
  },
} as const;

function IconTile({ bg, color, borderColor, Icon }: (typeof TILES)[keyof typeof TILES]) {
  return (
    <Circle
      data-testid="notification-avatar"
      size={TILE_SIZE}
      flexShrink={0}
      bg={bg}
      color={color}
      border="1px solid"
      borderColor={borderColor}
    >
      <Icon width={16} height={16} aria-hidden="true" />
    </Circle>
  );
}

/**
 * Leading marker of a notification row. Rows that need the operator to act show
 * an amber warning tile, agent rows a lilac spark tile, everything else the
 * actor's avatar.
 */
export const NotificationAvatar = memo(function NotificationAvatar({
  actorName,
  avatarUrl,
  isAgent,
  needsAttention,
}: NotificationAvatarProps) {
  if (needsAttention) return <IconTile {...TILES.attention} />;
  if (isAgent) return <IconTile {...TILES.agent} />;

  return (
    <Avatar
      data-testid="notification-avatar"
      src={avatarUrl ?? undefined}
      name={actorName}
      // Explicit box size so actor rows line up with the agent and attention tiles.
      boxSize={TILE_SIZE}
      fontFamily="heading"
      flexShrink={0}
      css={{ '--avatar-font-size': '12.5px' }}
      {...INITIALS_TINT}
    />
  );
});
